import { formatSrpm } from '../../../dashboard/format';
import { formatCurrency, formatRatio } from '../../../../lib/format';
import { dashboardT } from '../../../../lib/messages';
import {
  IntegrationColumnId,
  integrationColumnLabel,
} from '../components/IntegrationsTable/integrationTableColumnConfig';
import { integrationStatusLabel, type Integration } from '../model';

enum IntegrationCsvColumnId {
  DemandPartner = 'demandPartner',
}

type ExportableIntegrationColumnId =
  Exclude<IntegrationColumnId, IntegrationColumnId.Actions> | IntegrationCsvColumnId.DemandPartner;

type CsvValue = (integration: Integration) => string;

const csvValues: Record<ExportableIntegrationColumnId, CsvValue> = {
  [IntegrationColumnId.Name]: (integration) => integration.name,
  [IntegrationColumnId.Status]: (integration) => integrationStatusLabel(integration.status),
  [IntegrationCsvColumnId.DemandPartner]: (integration) =>
    integration.demandPartner.displayName ?? integration.demandPartner.name ?? '',
  [IntegrationColumnId.Placements]: (integration) => String(integration.placementCnt ?? 0),
  [IntegrationColumnId.IntegrationType]: (integration) => integration.integrationType,
  [IntegrationColumnId.Srpm]: (integration) => formatSrpm(integration.srpm7d),
  [IntegrationColumnId.BidRatio]: (integration) => formatRatio(integration.bidRatio7d),
  [IntegrationColumnId.Revenue7d]: (integration) => formatCurrency(integration.revenue7d),
  [IntegrationColumnId.Ecpm7d]: (integration) => formatCurrency(integration.ecpm7d),
  [IntegrationColumnId.Qps]: (integration) => String(integration.qps ?? '-'),
  [IntegrationColumnId.SellerDomain]: (integration) => integration.schainAsi || '-',
  [IntegrationColumnId.Duplication]: (integration) =>
    integration.duplicateDemandIntegration ? dashboardT('common.yes') : dashboardT('common.no'),
  [IntegrationColumnId.Note]: (integration) => integration.notes ?? '',
};

const exportableColumnIds = new Set<string>(Object.keys(csvValues));

export function createIntegrationsCsv(
  columnIds: readonly string[],
  integrations: readonly Integration[],
): string {
  const exportColumnIds = columnIds.filter(isExportableColumnId);
  const header = exportColumnIds.map(csvColumnLabel);
  const rows = integrations.map((integration) =>
    exportColumnIds.map((id) => csvValues[id](integration)),
  );
  return [header, ...rows].map((row) => row.map(escapeCsvValue).join(',')).join('\r\n');
}

function isExportableColumnId(id: string): id is ExportableIntegrationColumnId {
  return exportableColumnIds.has(id);
}

function csvColumnLabel(id: ExportableIntegrationColumnId) {
  return id === IntegrationCsvColumnId.DemandPartner
    ? dashboardT('integrations.demandPartner')
    : integrationColumnLabel(id);
}

function escapeCsvValue(value: string): string {
  const safeText = /^[\t\r=+\-@]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(safeText) ? `"${safeText.replaceAll('"', '""')}"` : safeText;
}
